import { useEffect, useMemo, useReducer, useRef, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { Layers, Loader2, Save, Search, ShieldAlert, Sparkles } from "lucide-react";
import { toast } from "sonner";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/contexts/AuthContext";
import { fetchAdminConnectorTypes, type AdminConnectorType } from "@/lib/portalData";
import { PlatformAdminOnly } from "@/components/portal/PlatformAdminOnly";
import { LineDiff } from "@/components/portal/shared/LineDiff";
import { EnrichmentDraftQueue } from "@/components/portal/connectors/EnrichmentDraftQueue";
import { EnrichmentFieldEditor } from "@/components/portal/connectors/EnrichmentFieldEditor";
import { EnrichmentHistory } from "@/components/portal/connectors/EnrichmentHistory";
import {
  type QueueItem,
  EMPTY_QUEUE_STATE,
  queueReducer,
  runDraftAll,
} from "@/components/portal/connectors/enrichmentDraftQueue.reducer";
import {
  MAX_LARASOFT_ENRICHMENT_BYTES,
  buildInventoryFields,
  describeSaveError,
  draftProvenanceFor,
  mergeDraftProposal,
  parseEnrichmentPaste,
  serialiseForPaste,
  setFieldValue,
  setTableDescription,
  tableNamesForBatch,
  validateEnrichmentSize,
  validateEnrichmentStrings,
} from "@/components/portal/connectors/connectorEnrichmentJson";
import {
  connectorEnrichmentAdminApi,
  type ConnectorEnrichmentDraftBatch,
  type ConnectorEnrichmentDraftResponse,
  type EnrichmentEnvelope,
  type EnrichmentInventoryResponse,
  type EnrichmentSeriesResponse,
} from "@/services/api/connectorEnrichment";

export const Route = createFileRoute("/portal/platform/connector-enrichment")({
  component: ConnectorEnrichmentRoute,
});

function ConnectorEnrichmentRoute() {
  return (
    <PlatformAdminOnly
      backTo="/portal/platform"
      backLabel="Back to platform"
      message="Authoring the central (Larasoft) connector enrichment library is restricted to platform administrators."
    >
      <ConnectorEnrichmentPage />
    </PlatformAdminOnly>
  );
}

const EMPTY_ENVELOPE: EnrichmentEnvelope = { tables: {} };

// The connector-type table is sourced from the platform-admin endpoint
// (GET /connectors/types/admin), which caps `limit` at 100 — so we page
// until `pagination.total` is reached rather than assuming one page.
const TYPES_PAGE_SIZE = 100;
const MAX_TYPE_PAGES = 20;

const isArchivedType = (t: AdminConnectorType) => (t.Status ?? "").toUpperCase() === "ARCHIVED";

async function fetchAllConnectorTypes(token: string): Promise<AdminConnectorType[]> {
  const all: AdminConnectorType[] = [];
  for (let page = 0; page < MAX_TYPE_PAGES; page += 1) {
    const res = await fetchAdminConnectorTypes(token, {
      limit: TYPES_PAGE_SIZE,
      offset: page * TYPES_PAGE_SIZE,
      sort_by: "name",
      sort_order: "asc",
    });
    all.push(...res.items);
    if (res.items.length === 0 || all.length >= res.pagination.total) break;
  }
  return all;
}

type AcceptedProposal = { proposal: ConnectorEnrichmentDraftResponse["proposal"]; rewrite: boolean; modelName: string };

function ConnectorEnrichmentPage() {
  const { token } = useAuth();
  const [typeSlug, setTypeSlug] = useState("");
  const [types, setTypes] = useState<AdminConnectorType[]>([]);
  const [typesLoading, setTypesLoading] = useState(false);
  const [typesError, setTypesError] = useState<string | null>(null);
  const [typeSearch, setTypeSearch] = useState("");
  const [activeType, setActiveType] = useState<string | null>(null);

  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [series, setSeries] = useState<EnrichmentSeriesResponse | null>(null);
  const [inventory, setInventory] = useState<EnrichmentInventoryResponse | null>(null);
  const [batches, setBatches] = useState<ConnectorEnrichmentDraftBatch[]>([]);
  const [skipped, setSkipped] = useState<{ recordName: string; reason: string }[]>([]);
  const [batchesError, setBatchesError] = useState<string | null>(null);

  const [draft, setDraft] = useState<EnrichmentEnvelope>(EMPTY_ENVELOPE);
  // D-B8 — the last-SAVED active JSON, refreshed on load/save/restore.
  // NEVER the live editor `draft` — this is the fixed point `accepted`
  // proposals are folded onto to decide Provenance at save time.
  const [baseline, setBaseline] = useState<EnrichmentEnvelope>(EMPTY_ENVELOPE);
  const [accepted, setAccepted] = useState<AcceptedProposal[]>([]);
  const [rawText, setRawText] = useState<string>(serialiseForPaste(EMPTY_ENVELOPE));
  const [rawError, setRawError] = useState<string | null>(null);

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveMessage, setSaveMessage] = useState<string | null>(null);

  const [selectedBatchId, setSelectedBatchId] = useState<string | null>(null);
  const [rewrite, setRewrite] = useState(false);
  const [showOnlySelectedRecord, setShowOnlySelectedRecord] = useState(false);
  const [queue, dispatchQueue] = useReducer(queueReducer, EMPTY_QUEUE_STATE);
  const [reviewingBatchId, setReviewingBatchId] = useState<string | null>(null);
  const draftAllSignal = useRef<{ aborted: boolean }>({ aborted: false });

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    setTypesLoading(true);
    setTypesError(null);
    fetchAllConnectorTypes(token)
      .then((items) => {
        if (!cancelled) setTypes(items);
      })
      .catch((err: unknown) => {
        if (!cancelled) setTypesError(err instanceof Error ? err.message : "Could not load connector types.");
      })
      .finally(() => {
        if (!cancelled) setTypesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  // Active types first, archived last; the search box filters name and slug.
  const visibleTypes = useMemo(() => {
    const q = typeSearch.trim().toLowerCase();
    const matches = q
      ? types.filter((t) =>
          [t.DisplayName, t.Name, t.Type].some((v) => (v ?? "").toLowerCase().includes(q)),
        )
      : types;
    return [...matches].sort((a, b) => Number(isArchivedType(a)) - Number(isArchivedType(b)));
  }, [types, typeSearch]);

  const provenance = draftProvenanceFor(draft, baseline, accepted);

  const updateDraft = (next: EnrichmentEnvelope) => {
    setDraft(next);
    setRawText(serialiseForPaste(next));
    setRawError(null);
    setSaveMessage(null);
  };

  const load = async (slug: string) => {
    if (!token || !slug.trim()) return;
    const normalised = slug.trim().toLowerCase();
    // A post-save reload of the SAME type (handleSave calls `load(activeType)`
    // to refresh the baseline) must NOT wipe the draft queue — D-B10's
    // "drafted against version N; current is M" staleness badge only makes
    // sense if queue items survive the save that advances the version. Only
    // a genuine type switch (opening a different connector type) starts
    // fresh.
    const isTypeSwitch = normalised !== activeType;
    setLoading(true);
    setLoadError(null);
    setBatchesError(null);
    try {
      const [seriesResult, inventoryResult, batchesResult] = await Promise.all([
        connectorEnrichmentAdminApi.getSeries("connector-type", normalised, "larasoft", token),
        connectorEnrichmentAdminApi.getInventory("connector-type", normalised, token).catch(() => null),
        connectorEnrichmentAdminApi
          .draftBatches("connector-type", normalised, token)
          .catch((err) => {
            setBatchesError(describeSaveError(err));
            return null;
          }),
      ]);
      setSeries(seriesResult);
      setInventory(inventoryResult);
      const resolvedBatches = batchesResult?.batches ?? [];
      setBatches(resolvedBatches);
      setSkipped(batchesResult?.skipped ?? []);
      const activeJson = seriesResult.active?.json ?? EMPTY_ENVELOPE;
      setBaseline(activeJson);
      if (isTypeSwitch) {
        // Stop any "draft all unattended" run against the PREVIOUS type
        // before wiping its queue — round-3 gate-review: without this, the
        // old run keeps issuing model calls against the type just switched
        // away from (its Stop button has already vanished along with the
        // queue), and re-opening "Draft all unattended" on the new type
        // could start a second run concurrently with it.
        draftAllSignal.current.aborted = true;
        setSelectedBatchId(resolvedBatches[0]?.id ?? null);
        setAccepted([]);
        dispatchQueue({ type: "enqueueAll", batches: [], rewrite: false });
      }
      updateDraft(activeJson);
      setActiveType(normalised);
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : "Failed to load enrichment series");
    } finally {
      setLoading(false);
    }
  };

  const validationError =
    validateEnrichmentStrings(draft) ?? validateEnrichmentSize(draft, MAX_LARASOFT_ENRICHMENT_BYTES);

  const allFields = inventory ? buildInventoryFields(inventory.records, { centralType: draft }) : [];
  const selectedBatch = batches.find((b) => b.id === selectedBatchId) ?? null;
  const fields =
    showOnlySelectedRecord && selectedBatch ? tableNamesForBatch(allFields, selectedBatch) : allFields;
  // D17: "inventory fields" means the connector type's columns (the records
  // endpoint's properties, Disabled=0 only) — a table-level description row
  // is synthesised for editing but isn't part of that inventory, and a row
  // appended for a table the live inventory no longer has (`inInventory ===
  // false`, e.g. a seed-only table) isn't either — neither must inflate the
  // denominator, though both still render for editing. Coverage is always
  // computed over EVERY field, not the "show only selected record" filter.
  const coverageFields = allFields.filter((f) => f.kind === "column" && f.inInventory !== false);
  const enrichedCount = coverageFields.filter((f) => f.effectiveValue.trim().length > 0).length;
  const coveragePercent =
    coverageFields.length > 0 ? Math.round((enrichedCount / coverageFields.length) * 100) : 0;

  const handleFieldChange = (field: (typeof fields)[number], value: string) => {
    updateDraft(
      field.kind === "description"
        ? setTableDescription(draft, field.tableName, value)
        : setFieldValue(draft, field.tableName, field.columnName, value),
    );
  };

  const handleRawChange = (text: string) => {
    setRawText(text);
    setSaveMessage(null);
    const result = parseEnrichmentPaste(text, MAX_LARASOFT_ENRICHMENT_BYTES);
    if (result.ok) {
      setDraft(result.envelope);
      setRawError(null);
    } else {
      setRawError(result.error);
    }
  };

  const handleSave = async () => {
    if (!token || !activeType || rawError) return;
    if (validationError) {
      setSaveError(validationError);
      toast.error(validationError);
      return;
    }
    setSaving(true);
    setSaveError(null);
    setSaveMessage(null);
    try {
      await connectorEnrichmentAdminApi.createVersion(
        "connector-type",
        activeType,
        "larasoft",
        draft,
        token,
        provenance.provenance === "model"
          ? { provenance: "model", modelName: provenance.modelName }
          : { provenance: "human" },
      );
      setAccepted([]);
      await load(activeType);
      setSaveMessage("Saved a new version");
      toast.success("Saved a new version");
    } catch (err) {
      const message = describeSaveError(err);
      setSaveError(message);
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const handleRestore = async (version: number) => {
    if (!token || !activeType) return;
    try {
      await connectorEnrichmentAdminApi.restore("connector-type", activeType, "larasoft", version, token);
      await load(activeType);
      toast.success(`Restored version ${version}`);
    } catch (err) {
      toast.error(describeSaveError(err));
      throw err;
    }
  };

  const loadVersionForDiff = async (versionId: string): Promise<EnrichmentEnvelope> => {
    if (!token) throw new Error("Not authenticated");
    const version = await connectorEnrichmentAdminApi.getVersion(versionId, token);
    return version.enrichmentJson;
  };

  const handleDraftSelected = async () => {
    if (!token || !activeType || !selectedBatch) return;
    const batch = selectedBatch;
    // enqueueOne (not enqueueAll — round-2 gate-review) so a single "Draft
    // this record" click never discards ready-but-unreviewed proposals a
    // prior "draft all unattended" run left in the queue.
    dispatchQueue({ type: "enqueueOne", batch, rewrite });
    dispatchQueue({ type: "runStart" });
    dispatchQueue({ type: "start", batchId: batch.id });
    try {
      const response = await connectorEnrichmentAdminApi.draft(
        "connector-type",
        activeType,
        batch.tables,
        token,
        { rewrite },
      );
      dispatchQueue({
        type: "ready",
        batchId: batch.id,
        response,
        draftedAgainstVersion: series?.active?.version ?? null,
      });
      setReviewingBatchId(batch.id);
    } catch (err) {
      const message = describeSaveError(err);
      dispatchQueue({ type: "failed", batchId: batch.id, error: message });
      toast.error(message);
    } finally {
      dispatchQueue({ type: "stop" });
    }
  };

  const handleDraftAll = () => {
    if (!token || !activeType || batches.length === 0) return;
    dispatchQueue({ type: "enqueueAll", batches, rewrite });
    setReviewingBatchId(null);
    draftAllSignal.current = { aborted: false };
    void runDraftAll(
      {
        draft: (tables) =>
          connectorEnrichmentAdminApi.draft("connector-type", activeType, tables, token, { rewrite }),
        dispatch: dispatchQueue,
        currentActiveVersion: () => series?.active?.version ?? null,
      },
      batches,
      draftAllSignal.current,
    );
  };

  const handleStopDraftAll = () => {
    draftAllSignal.current.aborted = true;
  };

  const handleReviewQueueItem = (item: QueueItem) => setReviewingBatchId(item.batchId);

  const handleDiscardQueueItem = (batchId: string) => {
    dispatchQueue({ type: "discard", batchId });
    if (reviewingBatchId === batchId) setReviewingBatchId(null);
  };

  const handleAcceptReviewed = () => {
    const item = queue.items.find((i) => i.batchId === reviewingBatchId);
    if (!item?.response) return;
    // Use the rewrite mode this item was DRAFTED under, not the live
    // checkbox — round-2 gate-review: toggling the checkbox between
    // drafting and accepting must not change how the proposal merges.
    const itemRewrite = item.rewrite ?? false;
    updateDraft(mergeDraftProposal(draft, item.response.proposal, { rewrite: itemRewrite }));
    setAccepted((prev) => [
      ...prev,
      { proposal: item.response!.proposal, rewrite: itemRewrite, modelName: item.response!.modelName },
    ]);
    dispatchQueue({ type: "accept", batchId: item.batchId });
    setReviewingBatchId(null);
    toast.success(
      `Draft accepted — ${item.response.tablesDrafted} table(s), ${item.response.fieldsDrafted} field(s). Review, then Save.`,
    );
  };

  const reviewingItem = queue.items.find((i) => i.batchId === reviewingBatchId && i.status === "ready") ?? null;

  // One save bar rendered at both the top (sticky) and the bottom of the
  // series editor: same handler, same disabled/saving state.
  const renderSaveBar = (position: "top" | "bottom") => (
    <div
      data-testid={`save-bar-${position}`}
      className={
        position === "top"
          ? "sticky top-0 z-10 mb-4 flex items-center justify-between gap-3 border-b border-slate-100 bg-background/95 py-2 backdrop-blur"
          : "mt-4 flex items-center justify-between gap-3 border-t border-slate-100 pt-3"
      }
    >
      <div className="text-sm">
        {rawError ? null : saveError ? (
          <p className="text-rose-600" role={position === "bottom" ? "alert" : undefined}>
            {saveError}
          </p>
        ) : saveMessage ? (
          <p className="text-emerald-600">{saveMessage}</p>
        ) : provenance.provenance === "model" ? (
          <p className="text-sky-700">
            Unmodified model draft — saving will record it as model-drafted.
          </p>
        ) : null}
      </div>
      <Button
        type="button"
        className="gap-1.5"
        disabled={saving || !!rawError || !!validationError}
        onClick={handleSave}
      >
        {saving ? (
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
        ) : (
          <Save className="h-3.5 w-3.5" />
        )}
        Save new version
      </Button>
    </div>
  );

  return (
    <div className="container mx-auto space-y-6 py-6">
      <div>
        <h1 className="flex items-center gap-2 text-2xl font-semibold">
          <Layers className="h-6 w-6" />
          Connector enrichment
        </h1>
        <p className="text-muted-foreground mt-1 text-sm">
          Author the central (Larasoft) field descriptions every client's connector
          of a given type inherits. Client and per-connector Larasoft text are
          added on top (D10) — never replaced here.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Connector type</CardTitle>
          <CardDescription>
            Every connector type the platform knows. Search, then click Open to edit
            that type's central enrichment series.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="relative max-w-sm">
            <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              aria-label="Search connector types"
              data-testid="type-search"
              value={typeSearch}
              onChange={(e) => setTypeSearch(e.currentTarget.value)}
              placeholder="Search by name or slug"
              className="pl-8"
            />
          </div>
          {typesError && (
            <p role="alert" className="text-sm text-destructive">
              {typesError} You can still open a type by typing its slug below.
            </p>
          )}
          <div className="max-h-96 overflow-auto rounded-md border" data-testid="type-table">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Slug</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="w-24 text-right">
                    <span className="sr-only">Action</span>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {typesLoading && (
                  <TableRow>
                    <TableCell colSpan={4} className="text-sm text-muted-foreground">
                      <Loader2 className="mr-2 inline h-4 w-4 animate-spin" />
                      Loading connector types…
                    </TableCell>
                  </TableRow>
                )}
                {!typesLoading && visibleTypes.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4} className="text-sm text-muted-foreground">
                      {types.length === 0 ? "No connector types found." : "No connector types match your search."}
                    </TableCell>
                  </TableRow>
                )}
                {visibleTypes.map((t) => {
                  const slug = t.Type;
                  const archived = isArchivedType(t);
                  return (
                    <TableRow
                      key={t.Id ?? slug}
                      data-testid={`type-row-${slug.toLowerCase()}`}
                      className={archived ? "text-muted-foreground" : undefined}
                    >
                      <TableCell className="font-medium">{t.DisplayName || t.Name}</TableCell>
                      <TableCell>
                        <code className="text-xs">{slug}</code>
                      </TableCell>
                      <TableCell>
                        {archived ? <Badge variant="secondary">Archived</Badge> : <Badge variant="outline">Active</Badge>}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          type="button"
                          size="sm"
                          variant={activeType === slug.toLowerCase() ? "default" : "outline"}
                          aria-label={`Open ${slug}`}
                          disabled={loading || queue.running}
                          onClick={() => {
                            setTypeSlug(slug);
                            void load(slug);
                          }}
                        >
                          Open
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
          <div className="flex flex-wrap items-end gap-3">
            <div className="space-y-1">
              <Label htmlFor="type-slug" className="text-xs text-slate-500">
                Or type a slug
              </Label>
              <Input
                id="type-slug"
                value={typeSlug}
                onChange={(e) => setTypeSlug(e.currentTarget.value)}
                placeholder="xero"
                className="w-56"
              />
            </div>
            <Button
              aria-label="Open typed slug"
              onClick={() => void load(typeSlug)}
              disabled={loading || !typeSlug.trim() || queue.running}
            >
              {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Open
            </Button>
          </div>
          {queue.running && (
            <p className="text-xs text-muted-foreground">
              A draft is in progress — wait for it to finish (or Stop a "Draft all unattended" run below) before switching connector type.
            </p>
          )}
        </CardContent>
      </Card>

      {loadError && (
        <Card className="border-destructive">
          <CardContent className="flex items-center gap-2 py-4 text-sm text-destructive">
            <ShieldAlert className="h-4 w-4" />
            {loadError}
          </CardContent>
        </Card>
      )}

      {activeType && series && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between gap-3">
            <div>
              <CardTitle className="capitalize">{activeType}</CardTitle>
              <CardDescription>
                {series.active
                  ? `Active version ${series.active.version}`
                  : "No central series yet — the first save creates version 1."}
              </CardDescription>
            </div>
            {inventory && (
              <Badge variant="outline" data-testid="coverage-counter">
                {enrichedCount} / {coverageFields.length} fields documented ({coveragePercent}%)
              </Badge>
            )}
          </CardHeader>
          <CardContent>
            <Card className="mb-4 bg-muted/30">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Draft with model</CardTitle>
                <CardDescription>
                  Drafts one top-level record (and its child tables) per call — a large
                  record may be split into parts. Nothing is saved until you accept and
                  Save.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {batchesError && (
                  <p className="text-sm text-rose-600" role="alert">
                    Batch planning unavailable: {batchesError}
                  </p>
                )}
                {batches.length > 0 && (
                  <div className="flex flex-wrap items-end gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs text-slate-500">Record</Label>
                      <Select value={selectedBatchId ?? undefined} onValueChange={setSelectedBatchId}>
                        <SelectTrigger className="w-72">
                          <SelectValue placeholder="Choose a record" />
                        </SelectTrigger>
                        <SelectContent>
                          {batches.map((b) => (
                            <SelectItem key={b.id} value={b.id}>
                              {b.label} — {b.columnCount} column{b.columnCount === 1 ? "" : "s"}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <Checkbox
                        id="rewrite-checkbox"
                        checked={rewrite}
                        onCheckedChange={(checked) => setRewrite(checked === true)}
                      />
                      <Label htmlFor="rewrite-checkbox" className="text-xs font-normal">
                        Rewrite existing text too
                      </Label>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="gap-1.5"
                      disabled={!selectedBatch || queue.running}
                      onClick={() => void handleDraftSelected()}
                    >
                      <Sparkles className="h-3.5 w-3.5" />
                      Draft this record
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={queue.running}
                      onClick={handleDraftAll}
                    >
                      Draft all unattended
                    </Button>
                  </div>
                )}
                {skipped.length > 0 && (
                  <p className="text-xs text-muted-foreground">
                    Skipped:{" "}
                    {skipped
                      .map((s) => `${s.recordName} (${s.reason === "no-columns" ? "no columns" : "too large"})`)
                      .join(", ")}
                  </p>
                )}

                {queue.items.length > 0 && (
                  <EnrichmentDraftQueue
                    state={queue}
                    onReview={handleReviewQueueItem}
                    onDiscard={handleDiscardQueueItem}
                    onStop={handleStopDraftAll}
                    onClearFinished={() => dispatchQueue({ type: "clear" })}
                    activeVersion={series?.active?.version ?? null}
                  />
                )}

                {reviewingItem?.response && (
                  <Card className="border-sky-200 bg-sky-50/40" data-testid="draft-preview">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm">Model draft — review before accepting</CardTitle>
                      <CardDescription>
                        {reviewingItem.response.tablesDrafted} table(s), {reviewingItem.response.fieldsDrafted}{" "}
                        field(s) drafted by {reviewingItem.response.modelName}.
                        {reviewingItem.response.missingTables.length > 0 && (
                          <>
                            {" "}
                            The model did not cover: {reviewingItem.response.missingTables.join(", ")}
                            {" "}(existing text kept — draft again to retry just those).
                          </>
                        )}
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      {reviewingItem.draftedAgainstVersion != null &&
                        series?.active?.version != null &&
                        reviewingItem.draftedAgainstVersion !== series.active.version && (
                          <p className="flex items-center gap-1.5 text-xs text-amber-700" role="alert">
                            <ShieldAlert className="h-3.5 w-3.5 shrink-0" />
                            A save happened elsewhere since this was drafted — drafted against version{" "}
                            {reviewingItem.draftedAgainstVersion}, active is now {series.active.version}.
                            You can still accept it, but check the diff carefully: depending on what changed,
                            your save may be recorded as human-authored rather than model-drafted.
                          </p>
                        )}
                      <LineDiff
                        oldText={serialiseForPaste(draft)}
                        newText={serialiseForPaste(
                          mergeDraftProposal(draft, reviewingItem.response.proposal, {
                            rewrite: reviewingItem.rewrite ?? false,
                          }),
                        )}
                        leftLabel="Current"
                        rightLabel="Drafted"
                      />
                      <div className="flex justify-end gap-2">
                        <Button type="button" variant="outline" size="sm" onClick={() => setReviewingBatchId(null)}>
                          Close
                        </Button>
                        <Button type="button" size="sm" onClick={handleAcceptReviewed}>
                          Accept into editor
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </CardContent>
            </Card>

            {renderSaveBar("top")}

            <Tabs defaultValue="fields">
              <TabsList>
                <TabsTrigger value="fields">Fields</TabsTrigger>
                <TabsTrigger value="advanced">Advanced (raw JSON)</TabsTrigger>
                <TabsTrigger value="history">History</TabsTrigger>
              </TabsList>

              <TabsContent value="fields" className="space-y-4 pt-3">
                {!inventory ? (
                  <p className="text-sm text-muted-foreground">
                    Could not load the field inventory for this type — the type
                    slug may not correspond to a known connector type in b2c yet.
                    You can still author JSON directly on the Advanced tab.
                  </p>
                ) : (
                  <>
                    {selectedBatch && (
                      <div className="flex items-center gap-1.5">
                        <Checkbox
                          id="show-only-selected"
                          checked={showOnlySelectedRecord}
                          onCheckedChange={(checked) => setShowOnlySelectedRecord(checked === true)}
                        />
                        <Label htmlFor="show-only-selected" className="text-xs font-normal">
                          Show only {selectedBatch.label}
                        </Label>
                      </div>
                    )}
                    <EnrichmentFieldEditor
                      fields={fields}
                      editableLayer="centralType"
                      editableValueOf={(f) => f.centralValue}
                      readOnlyLayersOf={() => []}
                      onChange={handleFieldChange}
                      canEdit={true}
                    />
                  </>
                )}
              </TabsContent>

              <TabsContent value="advanced" className="space-y-2 pt-3">
                <Label htmlFor="central-raw-json" className="text-xs text-slate-500">
                  Raw central enrichment JSON.
                </Label>
                <Textarea
                  id="central-raw-json"
                  aria-label="Raw central enrichment JSON"
                  value={rawText}
                  onChange={(e) => handleRawChange(e.currentTarget.value)}
                  rows={20}
                  className="font-mono text-xs"
                  spellCheck={false}
                />
                {rawError && (
                  <p className="text-sm text-destructive" role="alert">
                    {rawError}
                  </p>
                )}
              </TabsContent>

              <TabsContent value="history" className="pt-3">
                <EnrichmentHistory
                  versions={series.versions}
                  activeJson={series.active?.json ?? null}
                  loadVersion={loadVersionForDiff}
                  onRestore={handleRestore}
                  canRestore={true}
                />
              </TabsContent>
            </Tabs>

            {renderSaveBar("bottom")}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
